import React from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Link, Redirect } from 'react-router-dom';
import { useAlert } from 'react-alert';
import { useIntl } from 'react-intl';
import { Formik } from 'formik';

import { UnderLay as Loading } from 'components/Loading';

// IMAGES IMPORTS
import Logo from 'assets/img/logo.png';
// LAYOUT IMPORTS
import Wrapper from 'container/layouts/Wrapper';
// COMPONENT IMPORTS
import Input from 'components/Form/Input';
import FormButton from 'components/Form/Button';
// VALIDAITON IMPORTS
import SignInSchema from 'validations/login';

import { loginRequest, clear_message_request } from 'redux/actions';

const Login = (props) => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const intl = useIntl();

  document.title = `REACHAFRIKA | ${intl
    .formatMessage({
      id: 'login.text'
    })
    .toUpperCase()}`;

  const { loading, user, error, token } = useSelector((state) => state.authReducers);

  React.useEffect(() => {
    let mounted = true;
    if (mounted) {
      if (error) {
        alert.show(error.code === 500 ? intl.formatMessage({ id: error?.mgs }) : error, {
          type: 'error',
          title: intl.formatMessage({
            id: 'error.title'
          })
        });
        dispatch(clear_message_request());
      }
    }

    return () => (mounted = false);
  }, [alert, dispatch, error, intl]);

  if (user && token) return <Redirect to={'/dashboard'} />;

  return (
    <>
      {loading ? <Loading /> : null}
      <Wrapper isAuthLayout={true} {...props}>
        <main className="flex justify-center py-10">
          <div className="absolute w-full top-40 max-w-xl mx-auto sm:px-6 lg:px-8">
            <div className="bg-white overflow-hidden sm:rounded-lg border border-gray-200">
              <div className="px-4 py-5 sm:px-6 text-center">
                <Link to="/" className="flex justify-center my-10">
                  <img className="h-6" src={Logo} alt="REACHAFRIKA" />
                </Link>
                <h3 className="text-3xl leading-6 font-bold my-4">
                  {intl.formatMessage({ id: 'signin.form.title' })}
                </h3>
                <p className="text-xs leading-5 text-gray-500">
                  {intl.formatMessage({ id: 'signin.form.sub-text' })}
                  <Link to="/signup">
                    <span className="text-ra-dark ml-1">
                      {intl.formatMessage({ id: 'signup.text' })}
                    </span>
                  </Link>
                </p>
                <Formik
                  initialValues={{
                    email: '',
                    password: ''
                  }}
                  validationSchema={SignInSchema}
                  onSubmit={(values, { resetForm, setSubmitting }) => {
                    dispatch(loginRequest(values));
                    resetForm();
                    setSubmitting(false);
                  }}
                >
                  {({
                    values,
                    errors,
                    touched,
                    dirty,
                    isSubmitting,
                    isValid,
                    handleChange,
                    handleBlur,
                    handleSubmit
                  }) => (
                    <form className="block" onSubmit={handleSubmit}>
                      <div className="flex flex-wrap -mx-4">
                        <div className="w-full mx-6 py-2">
                          <Input
                            type="email"
                            name="email"
                            placeholder="Email"
                            inputValue={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              errors.email &&
                              intl.formatMessage({
                                id: errors.email
                              })
                            }
                            touched={touched.email}
                          />
                        </div>
                        <div className="w-full mx-6 py-2">
                          <Input
                            type="password"
                            name="password"
                            placeholder={intl.formatMessage({
                              id: 'signin.form.placeholder.password'
                            })}
                            inputValue={values.password}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              errors.password &&
                              intl.formatMessage({
                                id: errors.password
                              })
                            }
                            touched={touched.password}
                            isPassField
                          />
                        </div>
                      </div>
                      <div className="text-sm leading-5">
                        <Link to="/forgot-password">
                          <span className="text-ra-dark ml-1">
                            {intl.formatMessage({
                              id: 'signin.form.forgot-password'
                            })}
                          </span>
                        </Link>
                      </div>
                      <div className="flex justify-center mt-4 mx-2">
                        <FormButton
                          title={intl.formatMessage({
                            id: 'login.text'
                          })}
                          color="bg-ra-dark"
                          disabled={!(dirty && isValid) || isSubmitting}
                          loading={isSubmitting}
                        />
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </main>
      </Wrapper>
    </>
  );
};

Login.propTypes = {
  history: PropTypes.object.isRequired
};

export default Login;
